import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Moon, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BedtimeContext = createContext(null);

export function BedtimeProvider({ children }) {
  const { user } = useAuth();
  const [bedtime, setBedtime] = useState({ active: false, remaining: -1, limit: 0, watched: 0 });
  const [showWarning, setShowWarning] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/bedtime/session`, { withCredentials: true });
      setBedtime(data);
      if (data.active && data.remaining <= 0) setShowWarning(true);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const tickEpisode = useCallback(async () => {
    if (!user) return;
    try {
      await axios.post(`${API}/bedtime/tick`, {}, { withCredentials: true });
      await refresh();
    } catch { /* ignore */ }
  }, [user, refresh]);

  const canWatch = bedtime.active ? bedtime.remaining > 0 : true;

  return (
    <BedtimeContext.Provider value={{ bedtime, canWatch, tickEpisode, refresh, showWarning, setShowWarning }}>
      {children}
      {/* Bedtime Warning Dialog */}
      <Dialog open={showWarning && bedtime.active && bedtime.remaining <= 0} onOpenChange={setShowWarning}>
        <DialogContent className="border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] max-w-sm" data-testid="bedtime-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Fredoka'] text-xl flex items-center gap-2">
              <Moon size={20} className="text-[#4AC6FF]" /> Time for Bed!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <div className="w-20 h-20 rounded-full bg-[#4AC6FF]/20 border-2 border-[#4AC6FF] flex items-center justify-center mx-auto mb-4">
              <Moon size={36} strokeWidth={2} className="text-[#4AC6FF]" />
            </div>
            <p className="text-base text-[#0A0A0A] font-['Fredoka'] font-bold mb-2">
              You've watched {bedtime.watched} episode{bedtime.watched !== 1 ? 's' : ''} today!
            </p>
            <p className="text-sm text-[#4B5563] font-['Nunito']">
              Your episode limit for today has been reached. Time to rest those eyes! Come back tomorrow for more adventures.
            </p>
          </div>
          <button
            onClick={() => setShowWarning(false)}
            className="neo-btn w-full py-3 rounded-xl bg-[#4AC6FF] text-[#0A0A0A] font-bold"
            data-testid="bedtime-ok-btn"
          >
            OK, Goodnight!
          </button>
        </DialogContent>
      </Dialog>
    </BedtimeContext.Provider>
  );
}

export function useBedtime() {
  const ctx = useContext(BedtimeContext);
  if (!ctx) return { bedtime: { active: false, remaining: -1 }, canWatch: true, tickEpisode: () => {}, refresh: () => {} };
  return ctx;
}

export function BedtimeIndicator() {
  const { bedtime } = useBedtime();
  if (!bedtime.active) return null;

  return (
    <div className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#4AC6FF]/10 border border-[#4AC6FF]/30" data-testid="bedtime-indicator">
      <Moon size={14} strokeWidth={2.5} className="text-[#4AC6FF]" />
      <span className="text-xs font-bold font-['IBM_Plex_Mono'] text-[#4AC6FF]">
        {bedtime.remaining > 0 ? `${bedtime.remaining} ep${bedtime.remaining !== 1 ? 's' : ''} left` : 'Limit reached'}
      </span>
      {bedtime.remaining <= 1 && bedtime.remaining > 0 && (
        <AlertTriangle size={12} className="text-[#FF6B00]" />
      )}
    </div>
  );
}
